import type { ShallowRef } from 'vue';
import type { BlockRootContext, BlockSerialized } from '#layers/block-engine/types/block';
import type { BlockChangeEvent, BlockEditorContext } from '#layers/block-engine/types/block-editor';
import { createEventHook, tryOnScopeDispose, useDebouncedRefHistory } from '@vueuse/core';
import { provide, readonly, shallowRef } from 'vue';
import { deepToRaw } from '#layers/base/public';
import { useBlockEditorBounds } from '#layers/block-engine/composables/block-editor/bounds';
import { useBlockEditorChildParentMap } from '#layers/block-engine/composables/block-editor/child-parent-map';
import { useBlockEditorDrag } from '#layers/block-engine/composables/block-editor/drag';
import { useBlockEditorInsertion } from '#layers/block-engine/composables/block-editor/insertion';
import { useBlockEditorMove } from '#layers/block-engine/composables/block-editor/move';
import { useBlockEditorRemove } from '#layers/block-engine/composables/block-editor/remove';
import { useBlockEditorRootContext } from '#layers/block-engine/composables/block-editor/root-context';
import { useBlockEditorSelection } from '#layers/block-engine/composables/block-editor/selection';
import { useBlockEditorTree } from '#layers/block-engine/composables/block-editor/tree';
import { useBlockUpdate } from '#layers/block-engine/composables/block-editor/update';
import { useBlockEditorVariables } from '#layers/block-engine/composables/block-editor/variable';
import { blockEditorContextKey } from '#layers/block-engine/composables/use-block-editor-context';
import { useTranslationUtils } from '#layers/i18n/public';

export interface OpenBlockOptions {
	/**
	 * Path of the route if the block is a space/page view.
	 */
	routePath?: string;

	/**
	 * When rendering the root block, a context object can be passed.
	 *
	 * In the block editor, we can list the possible properties of this context object to be presented to the user.
	 */
	contextProperties?: {
		[K in keyof BlockRootContext]?: BlockEditorContextProperty<K>;
	};
}

export interface BlockEditorContextProperty<TKey extends keyof BlockRootContext = keyof BlockRootContext> {
	/**
	 * Example value of the property used when evaluating props of the blocks in the editor.
	 */
	value?: BlockRootContext[TKey] | (() => BlockRootContext[TKey]);
	description?: string;
	icon?: string;
}

export function useBlockEditor() {
	const { translateString } = useTranslationUtils();
	const blockData: ShallowRef<BlockSerialized | null> = shallowRef(null);
	const openOptions = shallowRef<OpenBlockOptions | null>(null);

	const changeEvent = createEventHook<BlockChangeEvent>();

	// Compose all the block editor functionality from smaller composables

	const history = useDebouncedRefHistory(blockData, {
		capacity: 50,
		flush: 'sync', // Otherwise pausing is broken
		debounce: 1000,
	});
	history.pause();

	const { childParentMap, getParent } = useBlockEditorChildParentMap({
		blockData,
	});

	const {
		updateBlock,
		updateChildProp,
		useBlockModel,
	} = useBlockUpdate({
		blockData,
		changeEvent,
	});

	const bounds = useBlockEditorBounds();

	const selection = useBlockEditorSelection({
		blockData,
	});

	const rootContext = useBlockEditorRootContext({
		options: openOptions,
	});

	const insertion = useBlockEditorInsertion({
		blockData,
		updateBlock,
		selection,
		rootContext,
		translateString,
	});

	const { moveBlock } = useBlockEditorMove({
		blockData,
		updateBlock,
		childParentMap,
		bounds,
		getContext,
	});

	const { removeBlock, removeSelectedBlocks } = useBlockEditorRemove({
		blockData,
		updateBlock,
		childParentMap,
		selection,
		bounds,
		getContext,
	});

	const drag = useBlockEditorDrag({
		blockData,
		insertion,
		moveBlock,
		bounds,
		childParentMap,
	});

	const variables = useBlockEditorVariables({
		blockData,
	});

	const tree = useBlockEditorTree();

	// Block editor context provided to Block Builder components and composables

	const context: BlockEditorContext = {
		block: readonly(blockData),
		changeEvent,
		history,
		undo,
		redo,
		updateBlock,
		updateChildProp,
		moveBlock,
		removeBlock,
		removeSelectedBlocks,
		useBlockModel,
		selection,
		insertion,
		drag,
		variables,
		tree,
		bounds,
		rootContext,
		childParentMap,
		getParent,
		options: openOptions,
	};

	/**
	 * Used in block editor composables to lazily get the editor context.
	 */
	function getContext() {
		return context;
	}

	provide(blockEditorContextKey, context);

	// Undo / Redo

	function undo() {
		if (blockData.value && history.canUndo.value) {
			history.undo();

			changeEvent.trigger({
				block: blockData.value,
				changes: {},
			});
		}
	}

	function redo() {
		if (blockData.value && history.canRedo.value) {
			history.redo();

			changeEvent.trigger({
				block: blockData.value,
				changes: {},
			});
		}
	}

	// Open / Close block editor

	function openBlock<const TOptions extends OpenBlockOptions>(block: BlockSerialized, options: TOptions = {} as TOptions) {
		close();
		blockData.value = structuredClone(deepToRaw(block));
		openOptions.value = options;
		// History is paused so we need to manually set the current snapshot
		history.last.value.snapshot = blockData.value;
		history.resume();
	}

	function updateOptions(options: Partial<OpenBlockOptions>) {
		openOptions.value = {
			...openOptions.value,
			...options,
		};
	}

	function close() {
		history.pause();
		blockData.value = null;
		selection.clear();
		insertion.clear();
		history.clear();
	}

	tryOnScopeDispose(() => {
		close();
	});

	return {
		openBlock,
		updateOptions,
		close,
		context,
	};
}

export type UseBlockEditorReturn = ReturnType<typeof useBlockEditor>;
